import { offsetWithinEditable, textOffsetAtPoint } from '../../../utils/caretUtils';
import { deepBlockIdFor } from '../../../utils/blockSelectionUtils';
import { findBlock } from '../../../utils/blockUtils';
import { EDITABLE_TYPES } from '../../../utils/editorConstants';

export const DRAG_SELECT_PX = 10;

export const HANDLE_OR_ZONE = '.block-drag-handle, .block-drag-zone-extended';
export const EDITOR_MENUS = '.callout-menu, .block-handle-menu, .callout-columns';
export const NON_TEXT_BLOCK = '.divider-block, .image-block, .video-block, .snippet-block';
export const CLICK_AWAY_KEEP =
  '.block-drag-handle, .block-drag-zone-extended, .divider-block, .image-block, .video-block, .snippet-block';

/** Controls inside a linked variable row — clicks here edit, they don't select the block. */
const LINKED_ROW_FIELD =
  'input, textarea, select, button, a, label, [contenteditable="true"], .linked-variable-row-picker__item, .variables-panel__col-resize';

/**
 * Padding and labels of a linked-variable-row. The row is a block (select,
 * drag handle, Delete) but its fields stay editable.
 */
export function isLinkedRowChromeTarget(target) {
  if (!target?.closest) return false;
  const wrapper = target.closest('.block-wrapper');
  if (!wrapper || wrapper !== target.closest('[data-block-id]')) return false;
  if (!wrapper.querySelector(':scope > .linked-variable-row-block')) return false;
  const field = target.closest(LINKED_ROW_FIELD);
  if (field && wrapper.contains(field)) return false;
  return true;
}

/** Image/divider/snippet, or the non-field surface of a linked variable row. */
export function isSelectableBlockTarget(target) {
  if (!target?.closest) return false;
  return !!(target.closest(NON_TEXT_BLOCK) || isLinkedRowChromeTarget(target));
}

export const clearNativeSelection = () => {
  window.getSelection()?.removeAllRanges();
};

export const blurActiveEditable = () => {
  const active = document.activeElement;
  if (active?.isContentEditable || active?.tagName === 'TEXTAREA') {
    active.blur();
  }
};

export const blurContentEditable = () => {
  if (document.activeElement?.isContentEditable) {
    document.activeElement.blur();
  }
};

/** Drop focus from a text field so Delete/Backspace hit the selected block. */
export const blurFocusedField = () => {
  const active = document.activeElement;
  if (!active) return;
  if (
    active.isContentEditable ||
    active.tagName === 'INPUT' ||
    active.tagName === 'TEXTAREA' ||
    active.tagName === 'SELECT'
  ) {
    active.blur();
  }
};

export function selectionTargets(blocks, selectedBlockId, multiIds) {
  if (multiIds.length) {
    return multiIds.map((id) => findBlock(blocks, id)).filter(Boolean);
  }
  if (selectedBlockId) {
    return [findBlock(blocks, selectedBlockId)].filter(Boolean);
  }
  return [];
}

/**
 * True when a key/clipboard event should be left to the focused field.
 * A live multi-selection always outranks this: after a drag that STARTED
 * inside a text block, browsers can restore CE focus on mouseup — but with
 * blocks highlighted, C/X/Delete are block operations.
 * Non-editable block selection also wins over a leftover neighbour caret.
 */
export function isTypingInField(hasMulti, selectedBlock) {
  if (hasMulti) return false;
  const active = document.activeElement;
  const inField =
    active &&
    (active.isContentEditable || active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
  if (selectedBlock && !EDITABLE_TYPES.includes(selectedBlock.type)) {
    // A selected image still deletes even if a neighbour caret is left behind.
    // A linked row is both a block and a form: while a field inside THAT row
    // is focused, Delete edits the field.
    if (selectedBlock.type === 'linked-variable-row' && inField) {
      const ownerId = active.closest?.('[data-block-id]')?.getAttribute('data-block-id');
      if (ownerId === selectedBlock.id) return true;
    }
    return false;
  }
  return !!inField;
}

export function editableForBlock(root, blockId) {
  return blockId ? root.querySelector(`[data-block-id="${blockId}"] .doc-editable`) : null;
}

export function captureTextAnchorFromSelection(root, blockId) {
  const el = editableForBlock(root, blockId);
  const sel = window.getSelection();
  if (!el || !sel?.anchorNode || !el.contains(sel.anchorNode)) return null;
  return offsetWithinEditable(el, sel.anchorNode, sel.anchorOffset);
}

export function resolveTextAnchor(current, root, blockId, startX, startY) {
  if (current != null) return current;
  const fromSel = captureTextAnchorFromSelection(root, blockId);
  if (fromSel != null) return fromSel;
  const fromPoint = textOffsetAtPoint(editableForBlock(root, blockId), startX, startY);
  return fromPoint != null ? fromPoint : current;
}

export function textAnchorFromMouseDown(target, clickedId, clientX, clientY) {
  const editable = target.closest?.('.doc-editable');
  return editable && deepBlockIdFor(editable) === clickedId
    ? textOffsetAtPoint(editable, clientX, clientY)
    : null;
}

/** Probe the blocks column at `y` so lasso-select can start from empty margin. */
export function blockIdAtHeight(root, y) {
  const blocksEl = root.querySelector('.doc-editor-blocks');
  if (!blocksEl) return null;
  const r = blocksEl.getBoundingClientRect();
  const probed = deepBlockIdFor(document.elementFromPoint(r.left + r.width / 2, y));
  if (probed) return probed;
  let best = null;
  let bestDist = Infinity;
  blocksEl.querySelectorAll(':scope > [data-block-id]').forEach((el) => {
    const br = el.getBoundingClientRect();
    const dist = y < br.top ? br.top - y : y > br.bottom ? y - br.bottom : 0;
    if (dist < bestDist) {
      bestDist = dist;
      best = el.getAttribute('data-block-id');
    }
  });
  return best;
}

export const sameIdRange = (prev, range) =>
  prev.length === range.length && prev.every((id, i) => id === range[i]);
